import React, { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { ORDER_STATUS_COMPLETED } from '~/consts/order';
import { PROMO_PAYMENT_TYPE } from '~/consts/payment';

import { getPayments } from '~/services/PaymentsService';
import { formatDateShort, formatDateShortDateAndMonth, formatDateWithClock } from '~/services/TimeService';

import { Payment } from '~/types/services/payment';

import currencyFormatter from '~/utils/currencyFormatter';
import { calculateCredit, calculateDepositTotal, getSuccessfulItemsTotal } from '~/utils/order';
import { titleCase } from '~/utils/stringUtils';

import { getOrderItemDates } from '../../formatters/getOrderItemDates';
import { OrderStatusChip } from '../components/OrderStatusChip';

interface Props {
  order: App.Order;
}

/**
 * Extract locations from order items (accommodation and bedbank)
 */
function getOrderLocations(order: App.Order): Array<string> {
  const locations = new Set<string>();

  // From accommodation items
  order.items?.forEach((item) => {
    const location = item.offer?.location;
    if (location) {
      const locationStr = typeof location === 'string' ? location : location.description;
      if (locationStr) {
        locations.add(locationStr);
      }
    }
  });

  // From bedbank items
  order.bedbank_items?.forEach((item) => {
    if (item.offer?.name) {
      locations.add(item.offer.name);
    }
  });

  return Array.from(locations);
}

/**
 * Calculate order total including deposits and credits
 */
function getOrderTotal(order: App.Order): string {
  const credit = calculateCredit(order as any);
  const depositTotal = calculateDepositTotal(order as any);
  const total = order.status !== ORDER_STATUS_COMPLETED ? order.total : getSuccessfulItemsTotal(order as any);
  const finalTotal = total + depositTotal + credit;
  return finalTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function OrderCard({ order }: Props) {
  const [expanded, setExpanded] = useState(false);
  const locations = getOrderLocations(order);
  const orderTotal = getOrderTotal(order);
  const orderItemDates = getOrderItemDates(order);

  // Fetch payments when expanded (lazy loading)
  const {
    data: paymentsData,
    isLoading: isLoadingPayments,
    error: paymentsError,
  } = useQuery({
    queryKey: ['order-payments', order.id_orders],
    queryFn: () => getPayments(order.id_orders),
    enabled: expanded,
  });

  const payments = useMemo(() => paymentsData?.result || [], [paymentsData?.result]);

  return (
    <Card
      sx={{
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{ px: 0, pb: 0, '&:last-child': { pb: 0 } }}>
        {/* Header row: Date and Status */}
        <Stack direction="row" justifyContent="space-between">
          <Box>
            {/* Customer name */}
            <Typography variant="body2" fontWeight={600} noWrap title={order.customer_full_name}>
              {order.customer_full_name}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: '5px' }}>
              {formatDateWithClock(order.created_at)}
            </Typography>

            {/* Location */}
            {locations.length > 0 && (
              <Typography variant="body2" component="span">
                {locations.join(', ')}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <OrderStatusChip orderStatus={order.status} />

            {/* Order total and currency */}
            <Typography variant="body2" color="primary">
              {order.currency_code} {orderTotal}
            </Typography>

            <Link href={`/purchases/${order.id}`} target="_blank" rel="noreferrer" variant="body2">
              Open
            </Link>
          </Box>
        </Stack>

        {/* Expandable details */}
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0, m: 0 }}>
            <Typography variant="body2" fontWeight={500}>
              {expanded ? 'Hide Details' : 'Show Details'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {/* Check-in / Departure */}
            <Box mb={2}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Dates
              </Typography>
              {orderItemDates.length === 0 ? (
                <Typography>No dates</Typography>
              ) : (
                orderItemDates.map((orderItem, index) => {
                  const formatDateRange = () => {
                    if (orderItem.date === undefined) {
                      return 'No Date';
                    }
                    if (orderItem.endDate) {
                      return `${formatDateShortDateAndMonth(orderItem.date)} - ${formatDateShort(orderItem.endDate)}`;
                    }
                    return formatDateShort(orderItem.date);
                  };

                  return (
                    <Box key={`${orderItem.name}-${orderItem.type}-${index}`} sx={{ mb: 1 }}>
                      {orderItem.type !== 'accommodation' && orderItem.type !== 'experience' && (
                        <Typography fontWeight="bold" component="span" variant="body2">
                          {`${titleCase(orderItem.type.replace(/_/g, ' '))}:`}
                        </Typography>
                      )}
                      {(orderItem.type === 'accommodation' || orderItem.type === 'experience') && (
                        <Typography title={orderItem.name} variant="body2">
                          {orderItem.name}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={orderItem.status === 'cancelled' ? { textDecoration: 'line-through' } : undefined}
                      >
                        {formatDateRange()}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Transaction History */}
            <Box mb={2}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Transactions
              </Typography>
              {isLoadingPayments && <CircularProgress size={20} />}
              {paymentsError && <Typography color="error">Failed to load transactions</Typography>}
              {!isLoadingPayments && !paymentsError && payments.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No transactions
                </Typography>
              )}
              {!isLoadingPayments &&
                !paymentsError &&
                payments.map((payment: Payment) => {
                  const amount = parseFloat(payment.amount);
                  const formattedAmount = currencyFormatter(payment.currency, amount, 2);

                  let amountColor = 'text.primary';
                  if (amount < 0) {
                    amountColor = 'error.main';
                  } else if (amount > 0) {
                    amountColor = 'info.main';
                  }

                  return (
                    <Box
                      key={payment.id_payment}
                      sx={{
                        mb: 1.5,
                        pb: 1.5,
                        borderBottom: (theme) => `1px dashed ${theme.palette.divider}`,
                        '&:last-child': { borderBottom: 'none', mb: 0, pb: 0 },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {titleCase(payment.type.replace(/_/g, ' '))}
                          </Typography>
                          {payment.type === PROMO_PAYMENT_TYPE &&
                            'response_json' in payment &&
                            (('code_name' in payment.response_json && (
                              <Link
                                href={`/promos/code/${String(payment.response_json.code_name)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                              >
                                {String(payment.response_json.code_name)}
                              </Link>
                            )) ||
                              ('id_promo_code' in payment.response_json && (
                                <Link
                                  href={`/promos/code/${String(payment.response_json.id_promo_code)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="body2"
                                >
                                  {String(payment.response_json.id_promo_code)}
                                </Link>
                              )))}
                          <Typography variant="body2" color="text.secondary">
                            {formatDateWithClock(payment.created_at)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight={600} color={amountColor}>
                            {formattedAmount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {payment.intent}
                            {payment.status === 'failed' && ' (failed)'}
                            {payment.status === 'pending' && ' (pending)'}
                          </Typography>
                          {payment.psp_name && (
                            <Typography variant="body2" color="text.secondary">
                              {payment.psp_name}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      {payment.status === 'failed' && payment.response_json?.failure_reason && (
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          {String(payment.response_json.failure_reason)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
}
